import { MinusCircleOutlined } from '@ant-design/icons';
import { Empty, Input, Progress, Table, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import FilterDropdown from '../../foundation/components/filter_dropdown/FilterDropdown';
import LinkedVariantsTooltip from './LinkedVariantsTooltip';
import type { Sample } from './types';
import type { ProbandsSamplesColumnFilters } from './useProbandsSamplesTable';

type Props = {
  columnFilters: ProbandsSamplesColumnFilters;
  currentPage: number;
  entryDateBounds: {
    min: string | undefined;
    max: string | undefined;
  };
  entryDateFrom: string;
  entryDateTo: string;
  filterOptionsData?: Record<string, string[]>;
  handleFilterChange: (columnKey: string, values: string[]) => void;
  handleFilterClear: (columnKey: string) => void;
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, unknown>,
    sorter: SorterResult<Sample> | SorterResult<Sample>[],
  ) => void;
  isSamplesLoading: boolean;
  pageSize: number;
  samples: Sample[];
  setEntryDateFrom: (value: string) => void;
  setEntryDateTo: (value: string) => void;
  totalSamples: number;
};

const ProbandsSamplesTable = ({
  columnFilters,
  currentPage,
  entryDateBounds,
  entryDateFrom,
  entryDateTo,
  filterOptionsData,
  handleFilterChange,
  handleFilterClear,
  handleTableChange,
  isSamplesLoading,
  pageSize,
  samples,
  setEntryDateFrom,
  setEntryDateTo,
  totalSamples,
}: Props) => {
  // Every cell is a real <Link> so Ctrl/Cmd+click, middle-click and the
  // browser's "Open link in new tab" context menu work like in variant search.
  const renderSampleLink = (value: React.ReactNode, record: Sample) => (
    <Link to={`/samples/${record.id}`} className="probands-samples__cell-link">
      {value}
    </Link>
  );

  const columns: ColumnsType<Sample> = useMemo(
    () => [
      {
        title: (
          <span>
            Sample id
            <FilterDropdown
              columnKey="sample_id"
              title="Sample ID"
              filterOptions={filterOptionsData?.sample_id || []}
              selectedValues={columnFilters.sample_id}
              onFilterChange={(values) =>
                handleFilterChange('sample_id', values)
              }
              onClear={() => handleFilterClear('sample_id')}
            />
          </span>
        ),
        dataIndex: 'sample_id',
        key: 'sample_id',
        sorter: true,
        render: (text, record) =>
          renderSampleLink(
            <span className="probands-samples__sample-link">{text}</span>,
            record,
          ),
      },
      {
        title: (
          <span>
            Proband
            <FilterDropdown
              columnKey="subject_external_id"
              title="Proband"
              filterOptions={filterOptionsData?.subject_external_id || []}
              selectedValues={columnFilters.subject_external_id}
              onFilterChange={(values) =>
                handleFilterChange('subject_external_id', values)
              }
              onClear={() => handleFilterClear('subject_external_id')}
            />
          </span>
        ),
        dataIndex: ['subject', 'externalid'],
        key: 'subject_external_id',
        sorter: true,
        render: (text, record) =>
          renderSampleLink(
            <span className="probands-samples__subject-link">{text}</span>,
            record,
          ),
      },
      {
        title: (
          <span>
            Indication
            <FilterDropdown
              columnKey="indication"
              title="Indication"
              filterOptions={filterOptionsData?.indication || []}
              selectedValues={columnFilters.indication}
              onFilterChange={(values) =>
                handleFilterChange('indication', values)
              }
              onClear={() => handleFilterClear('indication')}
            />
          </span>
        ),
        dataIndex: 'indication',
        key: 'indication',
        sorter: true,
        render: (text: string | null, record) =>
          renderSampleLink(
            text ? (
              <div
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
              />
            ) : (
              '-'
            ),
            record,
          ),
      },
      {
        title: (
          <span>
            Reporter
            <FilterDropdown
              columnKey="reporter_display_name"
              title="Reporter"
              filterOptions={filterOptionsData?.reporter_display_name || []}
              selectedValues={columnFilters.reporter_display_name}
              onFilterChange={(values) =>
                handleFilterChange('reporter_display_name', values)
              }
              onClear={() => handleFilterClear('reporter_display_name')}
            />
          </span>
        ),
        dataIndex: ['reporter', 'display_name'],
        key: 'reporter_display_name',
        sorter: true,
        render: renderSampleLink,
      },
      {
        title: (
          <span>
            Entry date
            <FilterDropdown
              columnKey="entry_date"
              title="Entry Date"
              filterOptions={filterOptionsData?.entry_date || []}
              selectedValues={columnFilters.entry_date}
              onFilterChange={(values) =>
                handleFilterChange('entry_date', values)
              }
              onClear={() => handleFilterClear('entry_date')}
              isActive={Boolean(entryDateFrom || entryDateTo)}
              extraContent={
                <div className="filter-dropdown__date-range">
                  <div className="filter-dropdown__date-range-row">
                    <Input
                      type="date"
                      size="small"
                      value={entryDateFrom}
                      min={entryDateBounds.min}
                      max={entryDateBounds.max}
                      onChange={(e) => {
                        setEntryDateFrom(e.target.value);
                      }}
                      className="filter-dropdown__date-input"
                    />
                    <span>-</span>
                    <Input
                      type="date"
                      size="small"
                      value={entryDateTo}
                      min={entryDateBounds.min}
                      max={entryDateBounds.max}
                      onChange={(e) => {
                        setEntryDateTo(e.target.value);
                      }}
                      className="filter-dropdown__date-input"
                    />
                  </div>
                </div>
              }
            />
          </span>
        ),
        dataIndex: 'created',
        key: 'entry_date',
        sorter: true,
        render: renderSampleLink,
      },
      {
        title: (
          <span>
            Variants selected
            <FilterDropdown
              columnKey="linked_variants"
              title="Linked Variants"
              filterOptions={filterOptionsData?.linked_variants || []}
              selectedValues={columnFilters.linked_variants}
              onFilterChange={(values) =>
                handleFilterChange('linked_variants', values)
              }
              onClear={() => handleFilterClear('linked_variants')}
            />
          </span>
        ),
        key: 'linked_variants_count',
        width: 120,
        sorter: true,
        render: (_, record) => (
          <LinkedVariantsTooltip
            variants={record.genotype_variants || []}
            count={record.linked_variants_count}
            showLinks={true}
          />
        ),
      },
      {
        title: 'Progress',
        key: 'interpretation_progress',
        width: 200,
        render: (_, record) => {
          const progress = record.interpretation_progress;
          if (!progress || progress.total === 0) {
            return (
              <div className="probands-samples__progress-empty">
                <MinusCircleOutlined />
                <span>No variants</span>
              </div>
            );
          }
          return (
            <Tooltip
              title={`${progress.completed} of ${progress.total} variants accepted`}
            >
              <Progress
                percent={progress.percentage}
                size="small"
                strokeColor="#45606b"
                format={() => (
                  <strong>{`${progress.completed}/${progress.total}`}</strong>
                )}
                className="probands-samples__progress-bar"
              />
            </Tooltip>
          );
        },
      },
    ],
    [
      columnFilters,
      filterOptionsData,
      handleFilterChange,
      handleFilterClear,
      entryDateFrom,
      entryDateTo,
      entryDateBounds,
    ],
  );

  return (
    <Table
      size="large"
      columns={columns}
      dataSource={samples}
      loading={isSamplesLoading}
      onChange={handleTableChange}
      pagination={{
        current: currentPage,
        total: totalSamples,
        pageSize,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} samples`,
      }}
      locale={{ emptyText: <Empty description="No samples found" /> }}
      rowKey="id"
    />
  );
};

export default ProbandsSamplesTable;
